import type {Filters} from '../types/Filters';

/**
 * Normalizes a filter object from the BE `getDataQuery` response into the
 * internal store format used by EagGrid filter widgets.
 *
 * ## The two formats
 *
 * BE may return filters in either format (both are encountered in production):
 *
 * **Flat (unwrapped):**
 * ```json
 * { "vehicleOwnershipManager": ["b1d4fb90-..."] }
 * ```
 *
 * **Store (wrapped):**
 * ```json
 * { "vehicleOwnershipManager": { "value": ["b1d4fb90-..."] } }
 * ```
 *
 * All EagGrid filter widgets read their value as `props.value['value']`, so
 * the store must always hold the wrapped form. A flat value from the BE
 * would leave `props.value['value']` as `undefined`, making the filter appear
 * empty even though the data is filtered.
 *
 * ## Detection heuristic
 *
 * An entry is considered **already wrapped** when its value is a plain object
 * that contains the key `"value"`. This matches the convention used throughout
 * EagGrid (`unwrapFilterValue` in `isFilterValueActive.ts`).
 *
 * Everything else (arrays, primitives, date-range objects `{from, to}`) is
 * treated as flat and wrapped in `{ value: X }`.
 *
 * ## React Compiler / performance notes
 *
 * - Pure function — no React, no store access, no side effects.
 * - Single-pass loop — no chained .map()/.reduce() allocations.
 * - Always returns a new object (safe for referential equality checks).
 *
 * @param filters - Raw `filters` object from the BE `GetDataQueryResponse`.
 * @returns Normalized filter state ready for `store.setFilterValues()`.
 */
export const normalizeFilterValues = (filters: Record<string, unknown>): Filters => {
  const result: Filters = {};

  for (const key in filters) {
    const raw = filters[key];

    // Already wrapped: plain object containing a "value" key.
    // Use the same heuristic as unwrapFilterValue / isFilterValueActive.
    if (
      typeof raw === 'object' &&
      raw !== null &&
      !Array.isArray(raw) &&
      'value' in (raw as Record<string, unknown>)
    ) {
      result[key] = raw as Record<string, unknown>;
    } else {
      result[key] = {value: raw};
    }
  }

  return result;
};
